import React, { useState, useEffect } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import ChatCard from './ChatCard';
import '../styles/chat.css';

const JobPostingDetails = ({ 
  selectedChannels = [], 
  postingDetails = {}, 
  onDetailsChange = null,
  onTriggerMessage = null 
}) => {
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';
  
  const [details, setDetails] = useState({
    employmentType: postingDetails.employmentType || '',
    workplaceType: postingDetails.workplaceType || '',
    jobLocation: postingDetails.jobLocation || '',
    applicationUrl: postingDetails.applicationUrl || '',
    applicantLocationPreference: postingDetails.applicantLocationPreference || '',
    screeningQuestions: postingDetails.screeningQuestions || [],
    skills: postingDetails.skills || [],
    salaryRange: postingDetails.salaryRange || '',
    currency: postingDetails.currency || '',
    payPeriod: postingDetails.payPeriod || '',
    hiringManager: postingDetails.hiringManager || '',
    recruiterName: postingDetails.recruiterName || '',
    visaSponsorship: postingDetails.visaSponsorship || '',
  });

  useEffect(() => {
    if (onDetailsChange) {
      onDetailsChange(details);
    }
  }, [details, onDetailsChange]);

  const handleFieldChange = (field, value) => {
    const newDetails = { ...details, [field]: value };
    setDetails(newDetails);
    // Don't auto-trigger - wait for user to press Enter or click submit
  };

  const handleSubmitDetails = () => {
    // Build message with basic details (Employment Type, Workplace Type, Job Location)
    const parts = [];
    if (details.employmentType) parts.push(`Employment Type - ${details.employmentType}`);
    if (details.workplaceType) parts.push(`Workplace Type - ${details.workplaceType}`);
    if (details.jobLocation) parts.push(`Job Location - ${details.jobLocation}`);
    
    if (parts.length > 0 && onTriggerMessage) {
      const message = parts.join('\n');
      onTriggerMessage(message);
    }
  };

  const handleKeyPress = (e, field) => {
    // If Enter is pressed in any of the basic detail fields, submit
    if (e.key === 'Enter' && ['employmentType', 'workplaceType', 'jobLocation'].includes(field)) {
      handleSubmitDetails();
    }
  };

  // Convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    const safe = (hex || '').replace('#', '');
    if (safe.length !== 6) return hex;
    const r = parseInt(safe.slice(0, 2), 16);
    const g = parseInt(safe.slice(2, 4), 16);
    const b = parseInt(safe.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const DetailCard = ({ title, children }) => (
    <ChatCard 
      variant="primary" 
      size="md" 
      rounded="xl" 
      className="chat_no_shadow"
      style={{
        backgroundColor: isDark ? hexToRgba(primaryColor, 0.15) : hexToRgba(primaryColor, 0.08),
        border: 'none',
      }}
    >
      <h6 
        className="chat_font_semibold chat_mb_sm"
        style={{ 
          fontSize: '14px',
          fontWeight: 600,
          color: isDark ? '#F9FAFB' : '#111827',
          marginBottom: '12px'
        }}
      >
        {title}
      </h6>
      {children}
    </ChatCard>
  );

  const DetailItem = ({ label, value, placeholder = '-', editable = false, field = null }) => (
    <div className="chat_mb_xs" style={{ marginBottom: '8px' }}>
      <span 
        className="chat_font_normal"
        style={{ 
          fontSize: '12px',
          color: isDark ? '#9CA3AF' : '#6B7280',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: '4px'
        }}
      >
        <span>{label}</span>
        {editable && field ? (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            onKeyPress={(e) => handleKeyPress(e, field)}
            placeholder={placeholder}
            className="px-2 py-1 chat_font_normal"
            style={{
              fontSize: '12px',
              border: 'none',
              backgroundColor: 'transparent',
              color: isDark ? '#F9FAFB' : '#111827',
              minWidth: '150px',
              flex: '1',
              maxWidth: '200px',
              outline: 'none',
            }}
          />
        ) : (
          <span style={{ color: isDark ? '#D1D5DB' : '#111827' }}>
            {value || placeholder}
          </span>
        )}
      </span>
    </div>
  );

  return (
    <div className="chat_spacing_sm">
      <div className="chat_grid_2col chat_gap_md">
        {/* Job Basics */}
        <DetailCard title="Job Basics">
          <DetailItem 
            label="Employment Type - " 
            value={details.employmentType} 
            editable={true}
            field="employmentType"
          />
          <DetailItem 
            label="Workplace Type - " 
            value={details.workplaceType} 
            editable={true}
            field="workplaceType"
          />
          <DetailItem 
            label="Job Location - " 
            value={details.jobLocation} 
            editable={true}
            field="jobLocation"
          />
        </DetailCard>

        {/* Application Setup */}
        <DetailCard title="Application Setup">
          <DetailItem label="How Candidates Apply -" value="" />
          <DetailItem 
            label="Application URL (if required) - " 
            value={details.applicationUrl} 
            editable={true}
            field="applicationUrl"
          />
          <DetailItem 
            label="Applicant Location Preference -" 
            value={details.applicantLocationPreference} 
            editable={true}
            field="applicantLocationPreference"
          />
        </DetailCard>

        {/* Screening Questions */}
        <DetailCard title="Screening Questions">
          <DetailItem label="- -" value="" />
        </DetailCard>

        {/* Skills */}
        <DetailCard title="Skills (Add 5-10 skills)">
          <DetailItem label="- -" value="" />
        </DetailCard>

        {/* Compensation */}
        <DetailCard title="Compensation">
          <DetailItem 
            label="Salary Range -" 
            value={details.salaryRange} 
            editable={true}
            field="salaryRange"
          />
          <DetailItem 
            label="Currency -" 
            value={details.currency} 
            editable={true}
            field="currency"
          />
          <DetailItem 
            label="Pay Period -" 
            value={details.payPeriod} 
            editable={true}
            field="payPeriod"
          />
        </DetailCard>

        {/* Hiring Team Visibility */}
        <DetailCard title="Hiring Team Visibility">
          <DetailItem 
            label="Hiring Manager -" 
            value={details.hiringManager} 
            editable={true}
            field="hiringManager"
          />
          <DetailItem 
            label="Recruiter name -" 
            value={details.recruiterName} 
            editable={true}
            field="recruiterName"
          />
        </DetailCard>

        {/* Compliance & Preferences */}
        <DetailCard title="Compliance & Preferences">
          <DetailItem 
            label="Willingness to sponsor visas - " 
            value={details.visaSponsorship} 
            editable={true}
            field="visaSponsorship"
          />
        </DetailCard>
      </div>
    </div>
  );
};

export default JobPostingDetails;

